// components/inpaintMaskGeometry.ts
import { MaskShape, Rect } from '#types/types';
import { getWorkspaceRasterDimensions } from './workspaceRasterUtils';

export type MaskPoint = { x: number; y: number };

export const distanceToSegment = (p: MaskPoint, a: MaskPoint, b: MaskPoint): number => {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
};

export const pointInPolygon = (p: MaskPoint, pts: MaskPoint[]): boolean => {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const a = pts[i];
    const b = pts[j];
    if (!a || !b) continue;
    const intersects = (a.y > p.y) !== (b.y > p.y) && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x;
    if (intersects) inside = !inside;
  }
  return inside;
};

const maskShapeHasContent = (shape: MaskShape): boolean =>
  shape.kind === 'polygon' ? shape.points.length >= 3 : shape.points.length >= 1;

export const inpaintMaskHasContent = (shapes: MaskShape[] | null | undefined): boolean => {
  if (!shapes || shapes.length === 0) return false;
  return shapes.some(maskShapeHasContent);
};

const isPointInsideMaskShape = (point: MaskPoint, shape: MaskShape): boolean => {
  const first = shape.points[0];
  if (!first) return false;

  if (shape.kind === 'polygon') {
    return shape.points.length >= 3 && pointInPolygon(point, shape.points);
  }

  const radius = Math.max(1, (shape.width ?? 1) / 2);
  if (shape.points.length === 1) {
    return Math.hypot(point.x - first.x, point.y - first.y) <= radius;
  }

  for (let index = 0; index < shape.points.length - 1; index += 1) {
    const a = shape.points[index];
    const b = shape.points[index + 1];
    if (a && b && distanceToSegment(point, a, b) <= radius) {
      return true;
    }
  }
  return false;
};

export const isPointInsideInpaintMask = (
  point: MaskPoint,
  shapes: MaskShape[] | null | undefined,
): boolean => {
  if (!shapes || shapes.length === 0) return false;
  return shapes.some((shape) => isPointInsideMaskShape(point, shape));
};

export const worldPointToWorkspaceRasterPoint = (
  worldPoint: MaskPoint,
  frameRect: Rect,
): MaskPoint | null => {
  if (frameRect.w <= 0 || frameRect.h <= 0) return null;
  const { scale } = getWorkspaceRasterDimensions(frameRect);
  return {
    x: (worldPoint.x - frameRect.x) * scale,
    y: (worldPoint.y - frameRect.y) * scale,
  };
};
